import type { ProfessionalTypeEnum } from '@gql/graphql';
import {
  Button,
  PressSection,
  ProLandingSectionContent,
  ScrollArowAnimated,
  Section,
  SectionTitle,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { JoinProInfoPageType } from '../JoinProInfo';
import { TestimonialCardContainer } from '../ProLanding/components/TestimonialCardContainer';
import type { TestimonialCardContainerItemData } from '../ProLanding/components/TestimonialCardContainer/types';
import { SECTION_IMAGE_PER_TYPE } from './assets';
import dog from './assets/dog.webp';
import separationGrayBottom from './assets/separation-gray-bottom.svg';
import separationGrayTop from './assets/separation-gray-top.svg';
import separationWhite from './assets/separation-white.svg';
import * as Styled from './styled';
import type { JoinProPageProps } from './types';

export * from './types';

const ORDERED_INFO_TYPES: Array<JoinProInfoPageType> = [
  JoinProInfoPageType.PROFILE,
  JoinProInfoPageType.SEO,
  JoinProInfoPageType.BOOKING,
  JoinProInfoPageType.AGENDA,
];

export const JoinProPage: FC<JoinProPageProps> = (props) => {
  const { type, formComponent, links } = props;

  const { translate } = useTranslation();
  const { translate: translateResource } = useTranslation({ keyPrefix: 'joinPro' });
  const { translate: translateWithType } = useTranslation({
    keyPrefix: `joinPro.${type}`,
  });

  const testimonialItems = useMemo((): any[] => {
    return Object.values(translateResource('testimonials.items', { returnObjects: true })).map(
      (item: any, index) =>
        ({
          name: item.name,
          text: item.text,
          image: item.image,
        } as TestimonialCardContainerItemData),
    );
  }, [translateResource]);

  return (
    <>
      <Styled.TopSection>
        <Styled.TopSeparation src={separationWhite} />
        <Styled.TopDecoration src={dog} />
        <Styled.TopContainer>
          <Styled.TopTitle>{translateWithType('title')}</Styled.TopTitle>
          <Styled.TopSubtitle>{translateWithType('subtitle')}</Styled.TopSubtitle>
          <Styled.TopSearchCard>
            <SectionTitle>{translateResource('formTitle')}</SectionTitle>
            {formComponent}
          </Styled.TopSearchCard>
        </Styled.TopContainer>

        <Styled.ScrollArrowWrapper>
          <ScrollArowAnimated />
        </Styled.ScrollArrowWrapper>
      </Styled.TopSection>

      <Section centerChildren>
        <SectionTitle>{translateWithType('titleAdvantages')}</SectionTitle>
      </Section>

      {ORDERED_INFO_TYPES.map((sectionType, index) => {
        const content = translateResource(`sections.${sectionType}`, {
          returnObjects: true,
        }) as any;
        return (
          <Section key={index}>
            <ProLandingSectionContent
              key={index}
              title={content.title}
              // titleColor={TITLE_COLOR_PER_PRO_TYPE[type]}
              // subtitleColor={BACKGROUND_COLOR_PER_PRO_TYPE[type]}
              contents={[
                { subtitle: content.subtitle1, text: content.text1 },
                { subtitle: content.subtitle2, text: content.text2 },
              ]}
              image={SECTION_IMAGE_PER_TYPE[sectionType]}
              imageOnLeft={index % 2 !== 0}
            >
              <Button color="blue" shrink link={links[sectionType]} size="large">
                {translate('common.buttons.knowMore')}
              </Button>
            </ProLandingSectionContent>
          </Section>
        );
      })}

      <Styled.StatsWrapper>
        <Styled.StatsSeparationTop src={separationGrayTop} />
        <Styled.StatsSeparationBottom src={separationGrayBottom} />
        <Styled.StatsContainer>
          <Styled.StatsItem>
            <Styled.StatsValue>{translateResource('stats.visitors.amount')}</Styled.StatsValue>
            {translateResource('stats.visitors.title')}
          </Styled.StatsItem>
          <Styled.StatsItem>
            <Styled.StatsValue>{translateResource('stats.pageViews.amount')}</Styled.StatsValue>
            {translateResource('stats.pageViews.title')}
          </Styled.StatsItem>
        </Styled.StatsContainer>
        <Styled.StatsLegend>{translateResource('stats.legend')}</Styled.StatsLegend>
      </Styled.StatsWrapper>

      <Styled.TestimonialsWrapper>
        <Section centerChildren>
          <SectionTitle
            color="purple"
            dangerouslySetInnerHTML={{ __html: translateResource('testimonials.title') }}
          />
          <TestimonialCardContainer items={testimonialItems} />
        </Section>
      </Styled.TestimonialsWrapper>

      <PressSection />
    </>
  );
};
